import React, { useState, useEffect } from 'react';
import { Pagination, Empty } from 'antd';
import ViewClassComponent from '../ViewClassComponent/ViewClassComponent';
import './ClassesList.css';
import axiosInstance from '../../Config/axiosConfig';

const PAGE_SIZE = 16;

function ClassesList({ currentFilters, searchQuery, isMobile }) {
  const [allClasses, setAllClasses] = useState([]);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [displayClasses, setDisplayClasses] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  // Check if filtering/searching is active
  const isFiltering = Boolean(
    (currentFilters && Object.keys(currentFilters).length > 0) || searchQuery
  );

  /* --------------------------------
     Reset page when filters/search change
  --------------------------------- */
  useEffect(() => {
    setCurrentPage(1);
  }, [currentFilters, searchQuery]);

  /* --------------------------------
     Fetch data when page or filters change
  --------------------------------- */
  useEffect(() => {
    fetchClasses();
  }, [currentPage, currentFilters, searchQuery]);

  const fetchClasses = async () => {
    setLoading(true);
    try {
      const payload = {
        pageNumber: currentPage,
      };

      // Apply filters
      if (currentFilters?.subjectId) {
        payload.subjectId = currentFilters.subjectId;
      }

      if (currentFilters?.educationClass) {
        payload.educationClass = currentFilters.educationClass;
      }

      if (currentFilters?.educationLevelId) {
        payload.educationClassLevel = currentFilters.educationLevelId;
      }

      console.log('Fetching classes with payload:', payload);

      const response = await axiosInstance.post(
        '/Public/get-paged-classes',
        payload
      );

      const data = response.data?.data;

      if (data?.classes) {
        let classes = data.classes;

        // Backup client-side price filtering
        if (currentFilters?.maxPrice) {
          classes = classes.filter(item => {
            const price = parseFloat(item.price) || 0;
            return price <= currentFilters.maxPrice;
          });
        }

        setAllClasses(classes);
        setTotalPages(data.pages || 1);

        console.log('Total pages from response:', data.pages);
      } else {
        setAllClasses([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
      setAllClasses([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  /* --------------------------------
     Client-side search filtering
  --------------------------------- */
  useEffect(() => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const filtered = allClasses.filter(item =>
        (item.title && item.title.toLowerCase().includes(query)) ||
        (item.instructor && item.instructor.toLowerCase().includes(query)) ||
        (item.education_class && item.education_class.toLowerCase().includes(query)) ||
        (item.description && item.description.toLowerCase().includes(query))
      );
      setFilteredClasses(filtered);
    } else {
      setFilteredClasses(allClasses);
    }
  }, [searchQuery, allClasses]);

  /* --------------------------------
     Decide what to display
  --------------------------------- */
  useEffect(() => {
    setDisplayClasses(searchQuery ? filteredClasses : allClasses);
  }, [searchQuery, filteredClasses, allClasses]);

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const WireframeLoader = () => (
    <div className="grid-container" aria-busy="true" aria-live="polite">
      {[...Array(PAGE_SIZE)].map((_, i) => (
        <div key={i} className="wireframe-card">
          <div className="wireframe-image pulse-animation" />
          <div className="wireframe-line pulse-animation" style={{ width: '60%' }} />
          <div
            className="wireframe-line pulse-animation"
            style={{ width: '80%', animationDelay: '0.2s' }}
          />
          <div
            className="wireframe-line pulse-animation"
            style={{ width: '70%', animationDelay: '0.4s' }}
          />
        </div>
      ))}
    </div>
  );

  return (
    <div className="classes-list-container">
      {loading ? (
        <WireframeLoader />
      ) : (
        <>
          {displayClasses.length > 0 ? (
            <>
              <div className="category-hor-list-grid">
                {displayClasses.map(classItem => (
                  <ViewClassComponent
                    key={classItem.id}
                    name={classItem.title}
                    classData={classItem}
                  />
                ))}
              </div>

              {/* ✅ Pagination now works for filtered & unfiltered */}
              {totalPages > 1 && (
                <div className="pagination">
                  <Pagination
                    current={currentPage}
                    total={totalPages * PAGE_SIZE}
                    pageSize={PAGE_SIZE}
                    onChange={handlePageChange}
                    showSizeChanger={false}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="empty-state-container">
              <div className="empty-state">
                <Empty
                  description={
                    isFiltering
                      ? 'No classes match your search criteria. Try adjusting your filters or search terms.'
                      : 'Classes coming soon, we will keep you updated.'
                  }
                />
                {isFiltering && (
                  <button
                    className="clear-search-button"
                    onClick={() => window.location.reload()}
                  >
                    Clear Search & Filters
                  </button>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ClassesList;
